import type { EntryMeta } from '@app-types/sys'
import { Article } from '@components/article'
import { Box } from '@components/box'
import { Breadcrumbs } from '@components/breadcrumbs'
import { Section } from '@components/layout/section'
import { VideoContent } from '@components/video-content'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { ArticleApps } from '@contentful-components/article-apps'
import { ArticleImage } from '@contentful-components/article-image'
import { ArticleParagraph } from '@contentful-components/article-paragraph'
import { ArticleQuote } from '@contentful-components/article-quote'
import { ArticleSection } from '@contentful-components/article-section'
import { ArticleThumbnails } from '@contentful-components/article-thumbnails'
import { Embedded } from '@contentful-components/embedded'
import { PageMeta } from '@contentful-components/page-meta'
import { RichText } from '@contentful-components/rich-text'
import { SectionOfThumbnails } from '@contentful-components/section-of-thumbnails'
import { SectionResolver } from '@contentful-components/section-resolver'
import { Video } from '@contentful-components/video'
import type { PageFragment } from '@graphql/fragments/PageFragment.graphql'
import type { ArticleKind } from '@graphql/hooks/article-page'
import { EMPTY_COLLECTION, EMPTY_SECTION } from '@graphql/placeholders'
import { webviewStore } from '@lib/store/ui/webview'
import { TEXT } from '@lib/utils/text'
import type { ArticleStaticProps } from '@server/helpers/article'
import { extendConsole } from '@shared/logging/console'
import type { LocalLink } from '@shared/util/link'
import { LINK } from '@shared/util/link'
import { parseISO } from 'date-fns'
import { useRouter } from 'next/router'
import { ArticleJsonLd, VideoJsonLd } from 'next-seo'
import type { FunctionComponent, JSX } from 'react'

const logger = extendConsole('article')

/**
 * Allow you to create an article page to be used under a specific path
 * @param page
 * @param kind
 * @param breadcrumbs breadcrumbs to add to this page
 * @param subtitle
 * @param firstParagraph
 * @param article
 * @param relatedArticles
 */
export const ArticlePageTemplate: FunctionComponent<Props> = ({
    page,
    kind,
    breadcrumbs = [],
    subtitle,
    firstParagraph,
    article,
    relatedArticles,
}) => {
    const router = useRouter()

    if (!article) {
        return null
    }

    const slug = router.query.slug as string

    const {
        title = '',
        author,
        date,
        coverImage,
        coverVideo,
        meta,
        sections: { items: sections } = EMPTY_COLLECTION,
    } = article

    const isVideoKind = kind === 'Video'

    return (
        <>
            {meta && <PageMeta id={meta.sys.id} meta={meta} name={title} />}

            {isVideoKind ? (
                <VideoJsonLd
                    name={title}
                    description={documentToPlainTextString(subtitle) || title}
                    thumbnailUrl={
                        coverImage?.url ||
                        'https://step.com/assets/og-image.png'
                    }
                    uploadDate={new Date(date).toISOString()}
                    contentUrl={
                        coverVideo?.files?.items[0]?.source ?? undefined
                    }
                    publisher={{
                        name: 'Step',
                        url: 'https://step.com',
                    }}
                />
            ) : (
                <ArticleJsonLd
                    headline={title}
                    image={coverImage?.url ? [coverImage.url] : []}
                    author={author?.name ?? ''}
                    datePublished={date}
                    dateModified={date}
                    description={documentToPlainTextString(subtitle)}
                    url={LINK.post(
                        slug,
                        kind,
                        webviewStore.isWebview,
                    ).serialize(router, {
                        canonical: true,
                    })}
                />
            )}
            <Section padHorizontal={!isVideoKind}>
                <Box pad={['vertical']}>
                    <Breadcrumbs
                        current={title}
                        links={breadcrumbs.map(({ text, link }) => ({
                            text,
                            href: link.route || link.serialize(router),
                            as: link.serialize(router),
                        }))}
                    />
                </Box>
            </Section>
            <Section padHorizontal={!isVideoKind}>
                <Article
                    title={title}
                    author={
                        author && {
                            ...author,
                            avatar: author.avatar?.url
                                ? author.avatar
                                : undefined,
                        }
                    }
                    cover={{
                        image: coverImage?.url ? coverImage : undefined,
                        element:
                            coverVideo &&
                            (isVideoKind ? (
                                <VideoContent
                                    {...coverVideo}
                                    poster={coverImage}
                                />
                            ) : (
                                <Video {...coverVideo} />
                            )),
                    }}
                    subtitle={<RichText source={{ json: subtitle }} />}
                    dateTime={parseISO(date)}
                    showShareButton
                >
                    <RichText source={{ json: firstParagraph }} />
                    {renderArticleBlocks(sections)}
                </Article>
            </Section>
            {relatedArticles.items.length > 0 && (
                <SectionOfThumbnails
                    sys={{ id: 'related-articles' }}
                    type='SectionOfThumbnails'
                    thumbnails={relatedArticles}
                    section={{
                        ...EMPTY_SECTION,
                        title: isVideoKind
                            ? TEXT.relatedVideos
                            : TEXT.relatedArticles,
                        background: {
                            label: 'step-off-white',
                        },
                    }}
                />
            )}
            <SectionResolver sections={page?.sections?.items ?? []} />
        </>
    )
}

const COMPONENTS_BY_TYPE = {
    Quote: ArticleQuote,
    PolicyContent: ArticleParagraph,
    ActionDownloadApp: ArticleApps,
    Icon: ArticleImage,
    Section: ArticleSection,
    Video: Video,
    SectionOfThumbnails: ArticleThumbnails,
    Embedded,
}

/** Renders an appropriate UI counterpart to Contentful fragment */
export function renderArticleBlocks(
    fragment: EntryMeta[],
): (JSX.Element | null)[] {
    return fragment.map((fragment) => {
        const type = fragment.type
        const Component =
            COMPONENTS_BY_TYPE[type as keyof typeof COMPONENTS_BY_TYPE]
        if (!Component) {
            logger.error(`No component for type ${type}`)
            return null
        }
        // @ts-ignore: key prop not present on Component props
        return <Component {...fragment} key={fragment.sys.id} />
    })
}

type Props = {
    page?: PageFragment
    kind: ArticleKind
    breadcrumbs?: { text: string; link: LocalLink }[]
} & ArticleStaticProps
